import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { partition } from 'lodash';
import ASQCheckbox from './ASQCheckbox';
import ASQImage from './ASQImage';
import ASQTextArea from './ASQTextArea';
import ASQRadio from './ASQRadio';

const ASQResponseTypes = {
  checkbox: ASQCheckbox,
  image: ASQImage,
  radio: ASQRadio,
  textarea: ASQTextArea,
};

// A single survey question. Wrapped in React.memo so that changing one answer
// only re-renders (and reconciles) the affected question instead of the whole
// survey tree. props are stable across an unrelated answer change: `answersById`
// is passed whole, but the memo comparison below narrows it to just the answers
// this question references, so an update elsewhere is a no-op here.
const ASQQuestion = ({
  asq, question, entities, answersById, qIdx, qLength, onChange, onDelete, deletingRef,
}) => {
  const responses = question.responseTypes.map(i => entities.responseTypes[i]);
  const [required, optional] = partition(responses, { inputType: 'radio' });
  const getAnswer = (r) => r.answer ? answersById[r.answer] : null;

  // Optional answers whose required radio is unanswered are orphans. Collected
  // during render (pure), dispatched from the effect after commit — never
  // dispatch a side effect during render.
  const orphans = [];
  optional.forEach((r) => {
    const disabled = required.some((req) => !getAnswer(req));
    const answer = getAnswer(r);
    if (disabled && answer) {
      orphans.push({ id: answer.id, asqResponseTypeId: r.id });
    }
  });

  useEffect(() => {
    orphans.forEach(({ id, asqResponseTypeId }) => {
      // deletingRef holds ids we already dispatched a DELETE for. An id is added
      // once and never removed — a 404 means the answer is already gone (the
      // desired end state), and any release would let a still-in-store answer
      // re-qualify as an orphan next render and re-fire the DELETE, recreating
      // the render -> dispatch -> RECEIVE -> re-render -> dispatch storm.
      if (deletingRef.current.has(id)) return;
      deletingRef.current.add(id);
      onDelete(id, { asqResponseTypeId, id });
    });
  });

  const renderInput = (r, rIdx, keyPrefix, disabled) => {
    const InputType = ASQResponseTypes[r.inputType];
    return (
      <InputType
        key={`${keyPrefix}-${rIdx}-${r.id}`}
        disabled={disabled}
        asq={asq}
        responseType={r}
        answer={getAnswer(r)}
        onChange={onChange}
        onDelete={onDelete}
      />
    );
  };

  const optionalDisabled = required.some((req) => !getAnswer(req));

  return (
    <div className='o-question row'>
      <div className='column shrink u-padding-right--zero'>
        <span className='o-question__number'>{qIdx + 1}</span>
      </div>
      <div className='column'>
        <div className='row'>
          <div className='c-survey-question__content'>
            <p className='u-margin-bottom--small'><b>{question.boldBody}</b></p>
            { question.body && <p>{question.body}</p> }
            { question.imageUrl &&
              <div className='o-question__image text-center u-padding-bottom--small'>
                <img alt='' src={question.imageUrl}></img>
              </div> }
            {optional.map((r, rIdx) => renderInput(r, rIdx, 'ro', optionalDisabled))}
          </div>
          <div className='c-survey-question__controls'>
            {required.map((r, rIdx) => renderInput(r, rIdx, 'rm', false))}
          </div>
        </div>
        {(qIdx !== qLength) && <hr className='u-hr--answer' />}
      </div>
    </div>
  );
};

ASQQuestion.propTypes = {
  asq: PropTypes.object.isRequired,
  question: PropTypes.object.isRequired,
  entities: PropTypes.object.isRequired,
  answersById: PropTypes.object.isRequired,
  qIdx: PropTypes.number.isRequired,
  qLength: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  deletingRef: PropTypes.object.isRequired,
};

// Re-render only when an answer to this question actually references changes.
// immer keeps stable references for untouched paths, so comparing the answer id
// each response points to (plus the answer object identity) skips questions the
// current answer change did not touch.
const answersEqual = (prev, next) => {
  const prevAnswers = prev.answersById || {};
  const nextAnswers = next.answersById || {};
  const ids = prev.question.responseTypes.map((i) => prev.entities.responseTypes[i].answer);
  const nextIds = next.question.responseTypes.map((i) => next.entities.responseTypes[i].answer);
  if (ids.length !== nextIds.length) return false;
  for (let k = 0; k < ids.length; k += 1) {
    if (ids[k] !== nextIds[k]) return false;
    if (prevAnswers[ids[k]] !== nextAnswers[nextIds[k]]) return false;
  }
  return prev.onChange === next.onChange &&
    prev.onDelete === next.onDelete &&
    prev.qIdx === next.qIdx &&
    prev.qLength === next.qLength;
};

export default React.memo(ASQQuestion, answersEqual);
